/**
 * The lesson (decision 0045): a small world made by hand, the core's (spec 4.6), in which a new
 * player learns the game by playing it, five steps and five moves. The real world draws it with
 * its models, mist and halftone and the core plays it by its rules; nothing of it is kept.
 *
 * A move the step does not need but the game allows is allowed. A tap that would wake a creature
 * is never played: the creature rises as one a member of a party fell to does, then the mist
 * closes over it again and the step goes on. A banner where no creature sleeps is taken down.
 * At the end the numbers the last banners settle go, as in the game, and the distance the ground
 * reaches is the result (Daniel, 2026-11-03: no creature rises under a banner, as none does in
 * the game).
 */
import {
  type Coord,
  Game,
  LESSON_SEED,
  LESSON_STEPS,
  LESSON_WORLD,
  type LessonStep,
  NEIGHBOURS,
} from "@mistsweeper/core";
import { FADE_MS, type WorldSource } from "@mistsweeper/renderer";

/** How long a creature woken by mistake stands before the mist closes over it again. */
const WOKE_MS = 1700;
/** How long a banner planted where no creature sleeps stands before it is taken down. */
const WRONG_BANNER_MS = 700;
/** The pause after a step's last move, so its ground opens or its banner stands, before the next. */
const NEXT_MS = 650;

/** What the lesson asks of the game around it. */
export interface LessonHost {
  /** A move changed the world: what it revealed, and the tile it was taken on. */
  changed(game: Game, revealed: readonly Coord[], at: Coord): void;
  /** A tap would have woken the creature at this tile; it rises and the player is told. */
  woke(at: Coord): void;
  /** The creature woken by mistake sinks and the mist closes over it, from now. */
  sink(at: Coord): void;
  /** The mist over the tile is the world's again. */
  settled(): void;
  /** A banner was planted where no creature sleeps. */
  noCreature(): void;
  /** A new step shows: its card, and the camera to its tiles. */
  step(index: number, step: LessonStep): void;
  /** A frame is wanted. */
  frame(): void;
}

export class Lesson {
  readonly game = Game.create({ seed: LESSON_SEED, mode: "endless", config: LESSON_WORLD });
  /** The step that shows, 0 to the last, which asks nothing. */
  index = 0;
  /** The creature woken by mistake, standing until the mist closes over it again. */
  private woken: Coord | null = null;
  private advancing = false;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private readonly host: LessonHost;
  /** What the renderer draws: the lesson's run, a creature woken by mistake as a fallen one. */
  readonly world: WorldSource;

  constructor(host: LessonHost) {
    this.host = host;
    const game = this.game;
    const up = (x: number, y: number) => this.woken?.x === x && this.woken?.y === y;
    this.world = {
      isRevealed: (x, y) => game.isRevealed(x, y),
      hasBanner: (x, y) => !up(x, y) && game.hasBanner(x, y),
      adjacent: (x, y) => game.adjacent(x, y),
      terrain: (x, y) => game.terrain(x, y),
      isChasm: (x, y) => game.isChasm(x, y),
      isClosed: (x, y) => game.isClosed(x, y),
      isSettled: (x, y) => !up(x, y) && game.isSettled(x, y),
      get status() {
        return game.status;
      },
      killer: null,
      isFallen: up,
    };
  }

  get step(): LessonStep {
    return LESSON_STEPS[this.index] as LessonStep;
  }

  /** Whether the lesson has come to its end, the step that asks nothing. */
  get ended(): boolean {
    return this.index === LESSON_STEPS.length - 1;
  }

  /** The first step. */
  start(): void {
    this.host.step(0, this.step);
  }

  /** A tap or, with `banner`, a hold, a secondary click or a tap in banner mode, on a tile. */
  act(tile: Coord, banner: boolean): void {
    if (this.ended || this.woken) return;
    const game = this.game;
    const { x, y } = tile;
    if (banner) {
      if (game.isRevealed(x, y)) return;
      const result = game.toggleBanner(x, y);
      if (!result.changed) return;
      this.host.changed(game, [], tile);
      if (game.hasBanner(x, y) && !game.isChasm(x, y)) {
        this.host.noCreature();
        this.later(WRONG_BANNER_MS, () => {
          if (!game.hasBanner(x, y)) return;
          game.toggleBanner(x, y);
          this.host.changed(game, [], tile);
        });
      }
      this.check();
      return;
    }
    if (game.isRevealed(x, y)) {
      // A chord, as in the game, but never past a banner where no creature sleeps.
      if (this.wrongBannerBy(x, y)) return;
      const result = game.chord(x, y);
      if (!result.changed) return;
      this.host.changed(game, result.revealed, tile);
      this.check();
      return;
    }
    if (game.hasBanner(x, y) || !this.inReach(x, y)) return;
    if (game.isChasm(x, y)) {
      this.wake(tile);
      return;
    }
    const result = game.reveal(x, y);
    if (!result.changed) return;
    this.host.changed(game, result.revealed, tile);
    this.check();
  }

  /** Stops what waits: the lesson is left. */
  dispose(): void {
    for (const timer of this.timers) clearTimeout(timer);
    this.timers.clear();
  }

  private later(ms: number, then: () => void): void {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      then();
    }, ms);
    this.timers.add(timer);
  }

  private inReach(x: number, y: number): boolean {
    return NEIGHBOURS.some(([dx, dy]) => this.game.isRevealed(x + dx, y + dy));
  }

  private wrongBannerBy(x: number, y: number): boolean {
    return NEIGHBOURS.some(
      ([dx, dy]) => this.game.hasBanner(x + dx, y + dy) && !this.game.isChasm(x + dx, y + dy),
    );
  }

  /** The creature rises, stands, and sinks again under the mist; the step goes on. */
  private wake(tile: Coord): void {
    this.woken = tile;
    this.host.woke(tile);
    this.host.frame();
    this.later(WOKE_MS, () => {
      this.host.sink(tile);
      this.later(FADE_MS + 100, () => {
        this.woken = null;
        this.host.settled();
        this.host.frame();
      });
    });
  }

  /** Whether the step's moves are done; if so, on to the next after a moment. */
  private check(): void {
    if (this.advancing || this.ended) return;
    const { reveal, banners } = this.step;
    const game = this.game;
    if (!reveal.every((t) => game.isRevealed(t.x, t.y))) return;
    if (!banners.every((t) => game.hasBanner(t.x, t.y))) return;
    this.advancing = true;
    this.later(NEXT_MS, () => {
      this.advancing = false;
      this.index += 1;
      this.host.step(this.index, this.step);
      this.host.frame();
    });
  }
}
